import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Badge, Breadcrumb, Button, Checkbox, FilterChip, Tag, Textarea, Tooltip } from '@indipia/ui-core';
import { useAppStore } from '@indipia/shared';
import { assigneeSummaryText, useMineOnlyState } from '@indipia/shared-features';
import { CUST_REQ_KEY, custReqApi } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { useWorklogStore } from '../store/worklogStore';
import { Toast } from '../components/Toast';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { ThumbImg } from '../components/ThumbImg';
import { IndiPhotoPickerModal, type PickedIndiPhoto } from '../components/IndiPhotoPickerModal';
import { WorkOrderTabs } from '../components/WorkOrderTabs';
import { PartnerAssignModal } from '../components/PartnerAssignModal';
import {
  INDI_PHOTO_AGENT_ID,
  KeywordChips,
  PhotoAttach,
  revokePreview,
  type LocalPhoto,
} from '../components/OrderAttachments';
import { PHOTO_POLICY, pickValidPhotos, photoDropNotice } from '../config/uploadPolicy';
import { partnerStatusView } from '../utils/partnerStatus';
import { BULK_PREVIEW_MESSAGE, bulkPreviewEmptyText, bulkPreviewTabLabels } from '../utils/bulkPreview.logic';
import { BulkTaskContentCard } from '../components/custReq/BulkTaskContentCard';
import { BulkOrderPreviewModal, type BulkPreviewTab } from '../components/BulkOrderPreviewModal';
import {
  CUST_REQ_ORDER_MESSAGE,
  bulkDoneText,
  bulkSummaryText,
  includedOf,
  missingContentText,
  receivedOnly,
  rowHasContent,
  toRefs,
  toggleExcluded,
} from '../utils/custReqOrder.logic';
import type {
  BulkCandidateSite,
  BulkCandidateTask,
  BulkDatePreset,
  BulkWorkOrderItem,
  WorkOrderPhotoMeta,
} from '../types/worklog.types';

/**
 * 일괄 작업요청 (REQ-1-31~35, API §4.1.3·§4.1.4).
 *
 * - 대상 현장: 시공완료가 아닌 현장 전부(준비중 · 시공중, 공사기간 시작 전인 착공 전날 현장 포함 — REQ-1-31, S11 CASE_3).
 *   worklogStore(basic-info)가 이미 시공완료를 뺀 목록(scheduleId·공사기간 포함)을 주므로 별도 조회 API 없음.
 *   공사스케줄이 없는 현장은 공정 목록을 만들 수 없어 목록에는 보이되 선택할 수 없다(안내 문구만).
 * - 작업일자 조건(REQ-1-32) + 현장 선택을 둘 다 채워야 candidates를 조회한다(§5.4.2 — FE가 API를 조합하지 않고 1회 조회).
 * - 발송은 건별 독립(부분성공 허용, REQ-1-35) — 성공한 건만 로컬에서 즉시 "발송됨"으로 갱신하고 선택은 초기화한다.
 */

const DATE_OPTIONS: { value: BulkDatePreset; label: string }[] = [
  { value: 'TODAY', label: '오늘' },
  { value: 'TOMORROW', label: '내일' },
  { value: 'DAY_AFTER', label: '모레' },
  { value: 'THIS_WEEK', label: '이번주' },
  { value: 'ALL', label: '전체' },
];

function fmtPeriod(start: string | null, end: string | null): string {
  const f = (s: string | null) => (s ? s.replaceAll('-', '.') : '?');
  return `${f(start)} ~ ${f(end)}`;
}

function fmtTaskDate(start: string | null, end: string | null): string {
  const f = (s: string) => {
    const d = new Date(s);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${d.getMonth() + 1}.${d.getDate()}(${days[d.getDay()]})`;
  };
  if (!start) return '-';
  return !end || end === start ? f(start) : `${f(start)} ~ ${f(end)}`;
}

function taskKey(siteId: number, taskId: number): string {
  return `${siteId}#${taskId}`;
}

/** 후보 공정의 반장 일정 태그 — 스케줄이 상태를 안 주면 null(태그 · 흐림 · 토스트 없음) */
function taskPartnerStatus(task: BulkCandidateTask) {
  return partnerStatusView({
    partnerId: task.assigneePartnerId,
    partnerStatus: task.partnerStatus,
    replyExpireDtm: task.replyExpireDtm,
    // '자재파트너가 시공' 공종은 '미배정'이 아니라 '자재파트너' 배지(작업요청 작성 화면과 같은 기준)
    constructByVendor: task.constructByVendor,
  });
}

/** 공사스케줄이 없는 현장 — 공정 목록을 만들 수 없어 선택하지 못하게 하고 이유를 현장 줄에 보인다. */
const NO_SCHEDULE_NOTICE = '공사스케줄이 없어요 · 인디플래너에서 만들어 주세요';

/**
 * SIF-1 '자재파트너가 시공' 공종은 그 공정의 자재파트너를 작업자로 채운다 — 시공파트너 미지정으로 보이지 않고,
 * 체크·발송도 일반 공정과 같은 흐름을 탄다. 자재파트너(발주)가 없으면 그대로 둔다(미지정 표시).
 */
function withMaterialAssignee(sites: BulkCandidateSite[]): BulkCandidateSite[] {
  return sites.map((s) => ({
    ...s,
    tasks: s.tasks.map((t) =>
      t.constructByVendor === true && t.assigneePartnerId == null && t.materialPartnerId != null
        ? {
            ...t,
            assigneePartnerId: t.materialPartnerId,
            assigneePartnerName: t.materialPartnerName ?? null,
            assigneeMobile: t.materialPartnerContact ?? null,
          }
        : t,
    ),
  }));
}

/** 미확정 공정을 체크할 때 알리는 문구 — 막지 않는다(체크 · 발송 가능). 전체선택 · 미발송만 선택에는 띄우지 않는다. */
const UNFIXED_PARTNER_TOAST = '반장님 일정 미확정 공정 · 그래도 보낼 수 있어요';

/** 다른 화면(나의 할일 · 홈 배너 등)에서 여는 주소 `?siteIds=1,2&datePreset=TOMORROW` — 알 수 없는 값은 무시한다(X-04). */
const DATE_PRESET_VALUES: readonly string[] = ['TODAY', 'TOMORROW', 'DAY_AFTER', 'THIS_WEEK', 'ALL'];
function parseDatePreset(raw: string | null): BulkDatePreset {
  return raw && DATE_PRESET_VALUES.includes(raw) ? (raw as BulkDatePreset) : 'TODAY';
}
function parseSiteIds(raw: string | null): number[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter((s) => /^\d+$/.test(s)).map(Number))];
}

export function WorkOrderBulkPage() {
  const worklogs = useWorklogStore((s) => s.worklogs);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    void useWorklogStore.getState().refresh();
  }, []);

  // REQ-1-31 · S11 CASE_3 — 시공완료가 아닌 현장 전부(준비중 · 시공중) 노출. basic-info 가 시공완료를 이미 뺐지만 방어적으로 한 번 더 거른다.
  // 공사스케줄이 없는 현장도 목록에는 나오되 선택은 못 한다(selectableSiteIds) — 일괄 발송할 공정이 없기 때문.
  const eligibleSites = useMemo(() => worklogs.filter((w) => w.progressStatus !== 'DONE'), [worklogs]);
  const selectableSiteIds = useMemo(
    () => new Set(eligibleSites.filter((w) => w.scheduleId != null).map((w) => w.siteId)),
    [eligibleSites],
  );

  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);

  // 주소의 siteIds(REQ-11-03) — 일괄 대상 중 선택할 수 있는(공사스케줄이 있는) 현장만 처음 한 번 선택해 준다. 기준정보가 아직 비어 있으면 도착한 뒤에 적용한다.
  const urlSiteIds = useMemo(() => parseSiteIds(searchParams.get('siteIds')), [searchParams]);
  const urlSitesApplied = useRef(false);
  useEffect(() => {
    if (urlSitesApplied.current || urlSiteIds.length === 0 || eligibleSites.length === 0) return;
    urlSitesApplied.current = true;
    const ids = urlSiteIds.filter((id) => selectableSiteIds.has(id));
    if (ids.length > 0) setSelectedSiteIds(ids);
  }, [urlSiteIds, eligibleSites, selectableSiteIds]);

  // '☑ 내 현장만'(2026-10-12 개선사항 D-10) — 직원 기본 체크, 사장님 기본 해제. 내 현장 먼저.
  // 다시 체크해도 이미 체크한 현장은 숨기지 않는다(선택이 화면에서 사라지지 않게).
  const [mineOnly, setMineOnly] = useMineOnlyState();
  const visibleSites = useMemo(() => {
    const ordered = [...eligibleSites.filter((w) => w.mine), ...eligibleSites.filter((w) => !w.mine)];
    return mineOnly ? ordered.filter((w) => w.mine || selectedSiteIds.includes(w.siteId)) : ordered;
  }, [eligibleSites, mineOnly, selectedSiteIds]);
  const [datePreset, setDatePreset] = useState<BulkDatePreset>(() => parseDatePreset(searchParams.get('datePreset')));
  const [candidates, setCandidates] = useState<BulkCandidateSite[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedTaskKeys, setSelectedTaskKeys] = useState<Set<string>>(new Set());
  // 모든 공종에 공통으로 덧붙일 내용(선택) — 서버가 각 공종 작업 요청내용 뒤에 이어 붙인다(S16-8)
  const [content, setContent] = useState('');
  // 공종별 작업 요청내용(직접 고친 것만 보관 — 없으면 작업 요청 메모가 기본값) · 이번에 빼기로 해제한 고객 요청 · 펼친 [내용 ▸] 카드
  const [taskBodies, setTaskBodies] = useState<Record<string, string>>({});
  const [excludedReqs, setExcludedReqs] = useState<Set<number>>(() => new Set());
  const [openCards, setOpenCards] = useState<Set<string>>(() => new Set());
  const qc = useQueryClient();
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [indiPhotoOpen, setIndiPhotoOpen] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [assignTarget, setAssignTarget] = useState<{ siteId: number; task: BulkCandidateTask } | null>(null);
  const [sending, setSending] = useState(false);
  // 토스트 — 기본은 안내(info), 반장 일정 미확정 알림만 경고(warning, D-09 §15.8 .toast-warning)
  const [toast, setToast] = useState<{ message: string; variant: 'info' | 'warning' } | null>(null);
  const showToast = (message: string, variant: 'info' | 'warning' = 'info') => setToast({ message, variant });
  const textRef = useRef<HTMLTextAreaElement>(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;

  const indiPhotoSubscribed = useAppStore((s) => s.subscriptions.some((sub) => sub.agentId === INDI_PHOTO_AGENT_ID));
  // 인디포토 사진첩은 현장 1곳 기준 팝업이라(공용 컴포넌트), 복수 현장 선택 시 첫 번째 선택 현장 기준으로 연다.
  const indiPhotoSite = eligibleSites.find((w) => w.siteId === selectedSiteIds[0]);

  const toggleSite = (siteId: number) => {
    if (!selectableSiteIds.has(siteId)) return;
    setSelectedSiteIds((prev) => (prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]));
  };

  const siteIdsKey = selectedSiteIds.join(',');
  useEffect(() => {
    if (selectedSiteIds.length === 0) {
      setCandidates([]);
      setSelectedTaskKeys(new Set());
      return;
    }
    const controller = new AbortController();
    setLoadingCandidates(true);
    setSelectedTaskKeys(new Set());
    worklogApi
      .getBulkCandidates(selectedSiteIds, datePreset, controller.signal)
      .then((res) => setCandidates(withMaterialAssignee(res.data.data.sites ?? [])))
      .catch(() => setCandidates([]))
      .finally(() => setLoadingCandidates(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteIdsKey, datePreset]);

  const allTasks = useMemo(
    () => candidates.flatMap((site) => site.tasks.map((task) => ({ site, task }))),
    [candidates],
  );
  const assignableTasks = useMemo(() => allTasks.filter((t) => t.task.assigneePartnerId != null), [allTasks]);
  const unsentAssignableTasks = useMemo(() => assignableTasks.filter((t) => !t.task.sent), [assignableTasks]);

  // 고객 요청 — 선택한 공종과 [내용 ▸]을 펼친 공종의 접수 상태 요청을 조회한다(전달 정합용 updatedAt 포함)
  const reqTaskIds = useMemo(() => {
    const ids = new Set<number>();
    for (const t of allTasks) {
      const k = taskKey(t.site.siteId, t.task.taskId);
      if (selectedTaskKeys.has(k) || openCards.has(k)) ids.add(t.task.taskId);
    }
    return [...ids];
  }, [allTasks, selectedTaskKeys, openCards]);
  const reqResults = useQueries({
    queries: reqTaskIds.map((id) => ({
      queryKey: [CUST_REQ_KEY, 'list', id],
      queryFn: ({ signal }: { signal: AbortSignal }) => custReqApi.list(id, signal),
      staleTime: 0,
      retry: false,
    })),
  });
  const reqByTask = useMemo(() => {
    const m = new Map<number, { items: ReturnType<typeof receivedOnly>; loading: boolean; error: boolean; refetch: () => void }>();
    reqTaskIds.forEach((id, i) => {
      const r = reqResults[i];
      if (!r) return;
      m.set(id, { items: receivedOnly(r.data), loading: r.isFetching, error: r.isError, refetch: () => void r.refetch() });
    });
    return m;
  }, [reqTaskIds, reqResults]);
  const bodyOf = (key: string, task: BulkCandidateTask) => taskBodies[key] ?? task.scheduleMemo ?? '';
  /** 이 공종에 함께 보낼 요청 수 — 조회가 끝났으면 체크한 건수, 아직이면 후보 응답의 건수 */
  const includedCountOf = (task: BulkCandidateTask) => {
    const q = reqByTask.get(task.taskId);
    return q && !q.error && !q.loading ? includedOf(q.items, excludedReqs).length : task.customerRequestCount ?? 0;
  };

  const allSelected =
    assignableTasks.length > 0 &&
    assignableTasks.every((t) => selectedTaskKeys.has(taskKey(t.site.siteId, t.task.taskId)));
  const unsentSelected =
    unsentAssignableTasks.length > 0 &&
    selectedTaskKeys.size === unsentAssignableTasks.length &&
    unsentAssignableTasks.every((t) => selectedTaskKeys.has(taskKey(t.site.siteId, t.task.taskId)));

  const toggleTask = (siteId: number, taskId: number) => {
    const key = taskKey(siteId, taskId);
    // 반장 일정이 확정되지 않은 공정을 새로 체크하면 알린다(체크 · 발송은 막지 않음)
    if (!selectedTaskKeys.has(key)) {
      const task = allTasks.find((t) => t.site.siteId === siteId && t.task.taskId === taskId)?.task;
      const status = task ? taskPartnerStatus(task) : null;
      if (status && !status.confirmed) showToast(UNFIXED_PARTNER_TOAST, 'warning');
    }
    setSelectedTaskKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedTaskKeys(
      allSelected ? new Set() : new Set(assignableTasks.map((t) => taskKey(t.site.siteId, t.task.taskId))),
    );
  };

  const toggleSelectUnsent = () => {
    setSelectedTaskKeys(
      unsentSelected ? new Set() : new Set(unsentAssignableTasks.map((t) => taskKey(t.site.siteId, t.task.taskId))),
    );
  };

  /** 시공파트너 지정 — 즉시 로컬 후보 목록에 반영(재조회 불필요, DEV_RULES). 실제 스케줄 write-back은 발송 시점(REQ-1-05 재사용). */
  const assignPartner = (partner: { partnerId: number; partnerName: string; mobile: string | null }) => {
    if (!assignTarget) return;
    const { siteId, task } = assignTarget;
    setCandidates((prev) =>
      prev.map((site) =>
        site.siteId !== siteId
          ? site
          : {
              ...site,
              tasks: site.tasks.map((t) =>
                t.taskId !== task.taskId
                  ? t
                  : {
                      ...t,
                      assigneePartnerId: partner.partnerId,
                      assigneePartnerName: partner.partnerName,
                      assigneeMobile: partner.mobile,
                      // 직접 지정은 일정까지 합의된 것으로 보고 발송 때 서버가 CONFIRMED 로 확정한다(WorkOrderService REQ-1-05) — 화면도 같게 보인다
                      partnerStatus: 'CONFIRMED',
                      partnerSentDtm: null,
                      replyExpireDtm: null,
                    },
              ),
            },
      ),
    );
    setAssignTarget(null);
  };

  const insertKeyword = (text: string) => {
    const ta = textRef.current;
    if (!ta) {
      setContent((c) => (c.endsWith('\n') || !c ? c + text + '\n' : `${c}\n${text}\n`));
      return;
    }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const after = content.slice(end);
    const needsBreak = !after.startsWith('\n');
    const inserted = needsBreak ? `${text}\n` : text;
    setContent(content.slice(0, start) + inserted + after);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + inserted.length + (needsBreak ? 0 : 1);
      ta.setSelectionRange(pos, pos);
    });
  };

  const addPhotos = (files: File[]) => {
    const result = pickValidPhotos(files, photos.length);
    if (result.accepted.length) {
      setPhotos((p) => [
        ...p,
        ...result.accepted.map((f): LocalPhoto => {
          const url = URL.createObjectURL(f);
          return { id: url, url, name: f.name, source: 'FILE', file: f };
        }),
      ]);
    }
    const notice = photoDropNotice(result);
    if (notice) showToast(notice);
  };

  const addIndiPhotos = (picked: PickedIndiPhoto[]) => {
    setIndiPhotoOpen(false);
    if (!picked.length) return;
    const room = Math.max(0, PHOTO_POLICY.maxCount - photos.length);
    const within = picked.filter((p) => !photos.some((x) => x.photoId === p.photoId)).slice(0, room);
    if (!within.length) return;
    setPhotos((prev) => [
      ...prev,
      ...within.map((p): LocalPhoto => ({
        id: `indi-${p.photoId}`,
        url: p.thumbUrl ?? '',
        name: `인디포토 ${p.photoId}`,
        source: 'INDI_PHOTO',
        photoId: p.photoId,
        fileId: p.fileId ?? null,
      })),
    ]);
    showToast(`인디포토 사진 ${within.length}장을 첨부했습니다.`);
  };

  const removePhoto = (id: string) => {
    setPhotos((p) => {
      const t = p.find((x) => x.id === id);
      if (t) revokePreview(t);
      return p.filter((x) => x.id !== id);
    });
  };

  const selectedCount = selectedTaskKeys.size;
  const resendCount = useMemo(
    () => allTasks.filter((t) => selectedTaskKeys.has(taskKey(t.site.siteId, t.task.taskId)) && t.task.sent).length,
    [allTasks, selectedTaskKeys],
  );

  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const selectedKeysStr = Array.from(selectedTaskKeys).sort().join(',');
  const photoKey = photos.map((p) => p.id).join(',');
  const bulkInputKey = JSON.stringify([taskBodies, [...excludedReqs].sort()]);
  useEffect(() => {
    setRequestKey(crypto.randomUUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeysStr, content, photoKey, bulkInputKey]);

  // 선택한 공종의 고객 요청 조회 상태 — 조회 중·실패면 요청을 빠뜨린 채 보내지 않도록 막는다
  const selectedRows = allTasks.filter((t) => selectedTaskKeys.has(taskKey(t.site.siteId, t.task.taskId)));
  const reqChecking = selectedRows.some((t) => reqByTask.get(t.task.taskId)?.loading !== false);
  const reqFailed = selectedRows.some((t) => reqByTask.get(t.task.taskId)?.error === true);
  const missingCount = selectedRows.filter(
    (t) => !rowHasContent(bodyOf(taskKey(t.site.siteId, t.task.taskId), t.task), content, includedCountOf(t.task)),
  ).length;
  const includedReqTotal = selectedRows.reduce((n, t) => n + includedCountOf(t.task), 0);
  const canSend = selectedCount > 0 && !reqChecking && !reqFailed && missingCount === 0;
  const hint =
    selectedSiteIds.length === 0
      ? '현장과 작업일자를 선택해주세요'
      : selectedCount === 0
        ? '작업공정을 1개 이상 선택해주세요'
        : reqFailed
          ? CUST_REQ_ORDER_MESSAGE.reqLoadFailed
          : reqChecking
            ? CUST_REQ_ORDER_MESSAGE.reqChecking
            : missingCount > 0
              ? missingContentText(missingCount)
              : bulkSummaryText(selectedCount, resendCount, includedReqTotal);

  /** 이 공종에 함께 전달할 요청 참조(체크 해제한 건 제외) — 없으면 undefined */
  const sendRefsOf = (task: BulkCandidateTask) => {
    const q = reqByTask.get(task.taskId);
    const refs = q ? toRefs(includedOf(q.items, excludedReqs)) : [];
    return refs.length ? refs : undefined;
  };

  // [공종별 미리보기](S11 CASE_8) — 선택한 공종마다 실제로 나갈 알림톡 본문. 열 때마다 서버가 새로 만든다(조회 전용 — 발송 · 고객 요청 전환 없음).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStatus, setPreviewStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [previewTabs, setPreviewTabs] = useState<BulkPreviewTab[]>([]);
  const previewAbort = useRef<AbortController | null>(null);
  useEffect(() => () => previewAbort.current?.abort(), []);
  const canPreview = selectedCount > 0 && !reqChecking && !reqFailed && !sending;

  const loadPreview = () => {
    previewAbort.current?.abort();
    const controller = new AbortController();
    previewAbort.current = controller;
    setPreviewStatus('loading');
    // 탭 순서 = 화면의 공종 순서(selectedRows)
    const rows = selectedRows;
    const labels = bulkPreviewTabLabels(rows.map((t) => ({ siteName: t.site.siteName, workTypeName: t.task.workTypeName })));
    worklogApi
      .previewBulkWorkOrders(
        {
          items: rows.map((t) => ({
            ref: taskKey(t.site.siteId, t.task.taskId),
            worklogId: t.site.worklogId,
            partnerId: t.task.assigneePartnerId ?? undefined,
            recipientName: t.task.assigneePartnerName ?? undefined,
            orderContent: bodyOf(taskKey(t.site.siteId, t.task.taskId), t.task).trim(),
            customerRequestCount: includedCountOf(t.task),
          })),
          orderContent: content.trim(),
          compName: useAppStore.getState().session?.compName ?? undefined,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        const bodyByRef = new Map(res.data.data.items.map((i) => [i.ref, i.body]));
        setPreviewTabs(
          rows.map((t, i) => {
            const key = taskKey(t.site.siteId, t.task.taskId);
            const reqCount = includedCountOf(t.task);
            return {
              key,
              label: labels[i],
              body: bodyByRef.get(key) ?? null,
              emptyText: bulkPreviewEmptyText(rowHasContent(bodyOf(key, t.task), content, reqCount)),
              customerRequestCount: reqCount,
            };
          }),
        );
        setPreviewStatus('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setPreviewStatus('error');
      });
  };
  const openPreview = () => {
    if (!canPreview) return;
    setPreviewOpen(true);
    loadPreview();
  };
  const closePreview = () => {
    previewAbort.current?.abort();
    setPreviewOpen(false);
  };

  const send = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      const items: BulkWorkOrderItem[] = allTasks
        .filter((t) => selectedTaskKeys.has(taskKey(t.site.siteId, t.task.taskId)))
        .map((t) => ({
          ref: taskKey(t.site.siteId, t.task.taskId),
          worklogId: t.site.worklogId,
          taskId: t.task.taskId,
          workTypeCode: t.task.workTypeCode,
          partnerId: t.task.assigneePartnerId as number,
          // 자재파트너면 수신자 이름은 담당자명 우선(서버 BulkWorkOrderService · 작성 화면과 같은 기준)
          recipientName:
            (t.task.constructByVendor === true && t.task.materialPartnerContactName?.trim()) || t.task.assigneePartnerName || undefined,
          mobile: t.task.assigneeMobile ?? undefined,
          orderContent: bodyOf(taskKey(t.site.siteId, t.task.taskId), t.task).trim(),
          customerRequests: sendRefsOf(t.task),
        }));

      const photoMeta = photos.map((p, i): WorkOrderPhotoMeta => {
        if (p.source === 'INDI_PHOTO') {
          return {
            photoSeq: i,
            source: 'INDI_PHOTO',
            photoId: p.photoId!,
            ...(p.fileId != null ? { fileId: p.fileId } : {}),
          };
        }
        const dot = p.name.lastIndexOf('.');
        return {
          photoSeq: i,
          source: 'FILE',
          fileName: p.name,
          fileExt: dot >= 0 ? p.name.slice(dot + 1).toLowerCase() : '',
          fileSize: p.file!.size,
        };
      });
      const photoFiles = photos.filter((p) => p.source === 'FILE' && p.file).map((p) => p.file as File);

      const res = await worklogApi.createWorkOrdersBatch({
        requestKey,
        items,
        orderContent: content.trim(),
        compName: useAppStore.getState().session?.compName ?? undefined,
        photos: photoMeta.length ? photoMeta : undefined,
        photoFiles: photoFiles.length ? photoFiles : undefined,
      });

      const result = res.data.data;
      const succeededRefs = new Set(result.items.filter((i) => i.status === 'SUCCESS').map((i) => i.ref));
      // 발송된 공정은 재조회 없이 즉시 "발송됨"으로 갱신 + 체크 해제(DEV_RULES).
      setCandidates((prev) =>
        prev.map((site) => ({
          ...site,
          tasks: site.tasks.map((t) =>
            succeededRefs.has(taskKey(site.siteId, t.taskId))
              ? { ...t, sent: true, lastSentDtm: new Date().toISOString() }
              : t,
          ),
        })),
      );
      setSelectedTaskKeys(new Set());
      photosRef.current.forEach(revokePreview);
      setPhotos([]);
      setContent('');
      setTaskBodies({});
      setExcludedReqs(new Set());
      setOpenCards(new Set());
      void qc.invalidateQueries({ queryKey: [CUST_REQ_KEY] });
      // DEV_RULES(§4.1.3) — 발송 후 토스트 안내만, 다른 화면으로 자동 이동하지 않음(모달로 막지 않는다).
      showToast(
        bulkDoneText(
          result.count.succeeded,
          result.count.failed,
          result.items.reduce((n, i) => n + (i.status === 'SUCCESS' ? (i.deliveredCustReqCount ?? 0) : 0), 0),
        ),
      );
    } catch {
      showToast('일괄 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <Breadcrumb items={[{ label: '작업요청' }, { label: '일괄 작성' }]} className="px-06 pt-04" />
      <WorkOrderTabs />

      <div className="flex min-h-0 flex-1 flex-col gap-06 overflow-y-auto px-06 py-05">
        {/* 현장 선택 (REQ-1-31) */}
        <section>
          <div className="mb-02 flex items-baseline gap-xs">
            <span className="text-body-sm font-bold text-text">현장 선택</span>
            <span className="text-body-sm font-bold text-error">*</span>
            {/* ☑ 내 현장만 — '현장 선택' 제목 줄 오른쪽(D-10) */}
            <Tooltip tip="내가 현장 담당자 또는 현장 관리자인 현장만 보기" tabIndex={-1} className="ml-auto">
              <Checkbox label="내 현장만" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            </Tooltip>
          </div>
          <div className="mb-03 text-caption text-text-muted">
            시공완료가 아닌 현장(준비중 · 시공중)을 모두 불러옵니다. 공사스케줄이 있는 현장만 선택할 수 있어요.
          </div>
          {eligibleSites.length > 0 && visibleSites.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface-raised px-06 py-09 text-center text-body-sm text-text-muted">
              내가 담당·관리하는 준비중 · 시공중 현장이 없습니다
            </div>
          ) : eligibleSites.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface-raised px-06 py-09 text-center text-body-sm text-text-muted">
              준비중 · 시공중인 현장이 없습니다.
            </div>
          ) : (
            <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised">
              {visibleSites.map((w, i) => {
                const checked = selectedSiteIds.includes(w.siteId);
                // 공사스케줄이 없는 현장 — 목록에는 보이되 흐리게 하고 선택하지 못하게 한다
                const selectable = selectableSiteIds.has(w.siteId);
                return (
                  <label
                    key={w.siteId}
                    style={i > 0 ? { borderTop: '1px solid var(--shell-bg)' } : undefined}
                    className={`flex items-center gap-03 px-05 py-04 text-body-sm font-semibold transition-colors ${
                      !selectable
                        ? 'cursor-not-allowed text-text-muted'
                        : checked
                          ? 'cursor-pointer bg-group/10 text-group'
                          : 'cursor-pointer text-text hover:bg-surface-hover'
                    }`}
                  >
                    <Checkbox checked={checked} disabled={!selectable} onChange={() => toggleSite(w.siteId)} />
                    <span className="flex min-w-0 flex-1 items-baseline gap-03">
                      <span className="truncate">
                        {w.customerName ? `${w.customerName} 님 · ` : ''}
                        {w.siteName}
                      </span>
                      {/* 현장명 오른쪽 같은 줄: 담당 ○○ 외 N · 관리 ○○(지정 순서 첫 번째) — 회색 캡션, 강조 없음 (D-10) */}
                      <span className="min-w-0 shrink truncate text-caption font-medium text-text-muted">
                        {assigneeSummaryText(w.chargeMembers, w.managerMembers)}
                      </span>
                    </span>
                    {!selectable && <Badge status="warning">{NO_SCHEDULE_NOTICE}</Badge>}
                    <span className="shrink-0 text-caption font-medium text-text-muted">
                      {fmtPeriod(w.constStartDate, w.constEndDate)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        {/* 작업일자 조건 (REQ-1-32) */}
        <section>
          <div className="mb-03 flex items-baseline gap-xs">
            <span className="text-body-sm font-bold text-text">작업일자 조건</span>
            <span className="text-body-sm font-bold text-error">*</span>
          </div>
          <div className="flex flex-wrap gap-sm">
            {DATE_OPTIONS.map((opt) => (
              <FilterChip key={opt.value} active={datePreset === opt.value} onClick={() => setDatePreset(opt.value)}>
                {opt.label}
              </FilterChip>
            ))}
          </div>
        </section>

        {/* 작업공정 리스트 (REQ-1-33) */}
        <section>
          {selectedSiteIds.length > 0 && (
            <div className="mb-03 flex items-center justify-between">
              <div className="text-body-sm font-bold text-text">
                작업공종 <span className="text-group">{allTasks.length}건</span>
              </div>
              {allTasks.length > 0 && (
                <div className="flex items-center gap-sm text-caption font-bold text-text-secondary">
                  <button type="button" onClick={toggleSelectAll} className="hover:text-group">
                    {allSelected ? '전체해제' : '전체선택'}
                  </button>
                  <span className="text-border">|</span>
                  <button type="button" onClick={toggleSelectUnsent} className="hover:text-group">
                    {unsentSelected ? '선택 해제' : '미발송만 선택'}
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedSiteIds.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-06 py-10 text-center text-body-sm text-text-muted">
              현장과 작업일자를 선택하면 해당하는 작업공정이 여기에 표시됩니다.
            </div>
          ) : loadingCandidates ? (
            <div className="rounded-lg border border-border bg-surface-raised px-06 py-10 text-center text-body-sm text-text-muted">
              조회 중...
            </div>
          ) : allTasks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-06 py-10 text-center text-body-sm text-text-muted">
              선택한 조건에 해당하는 작업공정이 없습니다.
            </div>
          ) : (
            <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised">
              {candidates
                .filter((site) => site.tasks.length > 0)
                .map((site, si) => (
                  <div key={site.siteId} className={si > 0 ? 'border-t border-border' : undefined}>
                    <div className="px-05 py-02 text-caption font-bold text-text-secondary" style={{ background: 'var(--shell-bg)' }}>
                      {site.siteName}
                    </div>
                    {site.tasks.map((task, ti) => {
                      const unassigned = task.assigneePartnerId == null;
                      const key = taskKey(site.siteId, task.taskId);
                      const checked = selectedTaskKeys.has(key);
                      // 반장 일정 상태 태그 — 반장이 없는 행은 아래 '시공파트너가 지정되지 않았습니다'가 같은 뜻이라 태그를 중복해서 달지 않는다
                      const status = unassigned ? null : taskPartnerStatus(task);
                      // 반장 일정이 확정이 아니거나 이미 발송된 행은 기간 · 반장 이름의 글자 굵기를 낮춰 한 단계 덜 강조한다.
                      // 목업은 opacity(.55 · .68)로 흐리게 하지만 12px 글자가 AA 명암비 4.5:1 에 못 미쳐(각 2.87 · 3.58:1) 쓰지 않는다 — D-07 §3.3.
                      // 색은 그대로라 대비는 유지되고, 발송됨 행은 바탕색(bg-surface)과 '발송됨' 태그가 함께 톤다운을 전한다.
                      const toneDown = (status != null && !status.confirmed) || task.sent;
                      return (
                        <div
                          key={task.taskId}
                          style={ti > 0 ? { borderTop: '1px solid var(--shell-bg)' } : undefined}
                          className={`flex items-center gap-03 px-05 py-04 ${
                            unassigned ? 'bg-warning/5' : task.sent ? 'bg-surface' : ''
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={unassigned}
                            onChange={() => toggleTask(site.siteId, task.taskId)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="mb-01 flex flex-wrap items-center gap-sm">
                              <Tag size="sm">{task.workTypeName}</Tag>
                              <span className={`text-caption ${toneDown ? 'font-normal' : 'font-semibold'} text-text-secondary`}>
                                {fmtTaskDate(task.startDate, task.endDate)}
                              </span>
                            </div>
                            {unassigned ? (
                              <span className="text-caption font-semibold text-warning">시공파트너가 지정되지 않았습니다</span>
                            ) : (
                              <div className="flex flex-wrap items-center gap-sm text-caption text-text-muted">
                                <span className={`${toneDown ? 'font-normal' : 'font-bold'} text-text-secondary`}>{task.assigneePartnerName}</span>
                                <span>{task.assigneeMobile}</span>
                                {/* 자재시공 공종은 status 가 '자재파트너'(info) 배지 — partnerStatusView(constructByVendor) */}
                                {status && <Badge status={status.status}>{status.label}</Badge>}
                              </div>
                            )}
                            {!unassigned && (
                              <>
                                <div className="mt-02 flex flex-wrap items-center gap-sm">
                                  {rowHasContent(bodyOf(key, task), content, includedCountOf(task)) ? (
                                    <Badge status="success">내용 ✓</Badge>
                                  ) : (
                                    <Badge status="mute">내용 없음</Badge>
                                  )}
                                  {includedCountOf(task) > 0 && <Badge status="warning">요청 {includedCountOf(task)}</Badge>}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-expanded={openCards.has(key)}
                                    onClick={() =>
                                      setOpenCards((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(key)) next.delete(key);
                                        else next.add(key);
                                        return next;
                                      })
                                    }
                                  >
                                    {CUST_REQ_ORDER_MESSAGE.bulkRowOpen} {openCards.has(key) ? '▾' : '▸'}
                                  </Button>
                                </div>
                                {openCards.has(key) && (
                                  <BulkTaskContentCard
                                    body={bodyOf(key, task)}
                                    onBodyChange={(v) => setTaskBodies((prev) => ({ ...prev, [key]: v }))}
                                    memo={task.scheduleMemo}
                                    items={reqByTask.get(task.taskId)?.items ?? []}
                                    excluded={excludedReqs}
                                    onToggleReq={(id) => setExcludedReqs((prev) => toggleExcluded(prev, id))}
                                    reqStatus={
                                      reqByTask.get(task.taskId)?.error ? 'error' : reqByTask.get(task.taskId)?.loading ? 'loading' : 'ready'
                                    }
                                    onRetryReq={() => reqByTask.get(task.taskId)?.refetch()}
                                  />
                                )}
                              </>
                            )}
                          </div>
                          {unassigned ? (
                            <Button variant="secondary" size="sm" onClick={() => setAssignTarget({ siteId: site.siteId, task })}>
                              시공파트너 추가
                            </Button>
                          ) : task.sent ? (
                            <Tag size="sm">발송됨</Tag>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          )}
        </section>

        {/* 전달할 작업내용 (REQ-1-34) */}
        <section>
          <div className="mb-02 flex items-baseline gap-xs">
            <span className="text-body-sm font-bold text-text">{CUST_REQ_ORDER_MESSAGE.bulkCommonLabel}</span>
          </div>
          <Textarea
            ref={textRef}
            fill
            textareaClassName="min-h-[90px] resize-none"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={CUST_REQ_ORDER_MESSAGE.bulkCommonPlaceholder}
          />
          <div className="mt-03">
            <KeywordChips onInsert={insertKeyword} />
          </div>
        </section>

        {/* 사진 첨부 (선택사항, REQ-1-34) */}
        <section>
          <div className="mb-03 flex items-baseline gap-xs">
            <span className="text-body-sm font-bold text-text">사진 첨부</span>
            <span className="text-caption text-text-muted">선택 사항</span>
          </div>
          <PhotoAttach
            photos={photos}
            onAdd={addPhotos}
            onRemove={removePhoto}
            onOpen={setLightbox}
            onOpenIndiPhoto={() => setIndiPhotoOpen(true)}
          />
        </section>
      </div>

      {/* 발송 액션 — 폼 하단 고정(WorkOrderCreatePage와 동일 패턴). 색상은 실측 목업(.bulk-send-btn=var(--p)) 그대로 primary. */}
      <div className="flex shrink-0 items-center justify-between gap-sm border-t border-border px-06 py-04">
        <span className="text-body-sm font-semibold text-text-secondary">{hint}</span>
        <div className="flex shrink-0 items-center gap-sm">
          <Button variant="secondary" size="sm" disabled={!canPreview} onClick={openPreview}>
            {BULK_PREVIEW_MESSAGE.button}
          </Button>
          <Button variant="primary" size="sm" disabled={!canSend || sending} onClick={() => void send()}>
            {sending ? '발송 중…' : '일괄 발송하기'}
          </Button>
        </div>
      </div>

      <BulkOrderPreviewModal open={previewOpen} onClose={closePreview} status={previewStatus} tabs={previewTabs} onRetry={loadPreview} />

      <PhotoLightbox
        photos={photos.map((p) => ({ url: p.url }))}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
        renderImage={(photo, style) =>
          photo.url.startsWith('blob:') ? (
            <img src={photo.url} alt="" style={style} draggable={false} />
          ) : (
            <ThumbImg url={photo.url} imgStyle={style} />
          )
        }
      />

      <IndiPhotoPickerModal
        open={indiPhotoOpen}
        siteId={indiPhotoSite?.siteId ?? null}
        siteName={indiPhotoSite?.siteName}
        subscribed={indiPhotoSubscribed}
        room={Math.max(0, PHOTO_POLICY.maxCount - photos.length)}
        onClose={() => setIndiPhotoOpen(false)}
        onConfirm={addIndiPhotos}
      />

      {assignTarget && (
        <PartnerAssignModal
          taskLabel={`${candidates.find((s) => s.siteId === assignTarget.siteId)?.siteName ?? ''} · ${fmtTaskDate(
            assignTarget.task.startDate,
            assignTarget.task.endDate,
          )} · ${assignTarget.task.workTypeName}`}
          onClose={() => setAssignTarget(null)}
          onAssign={assignPartner}
        />
      )}

      <Toast
        message={toast?.message ?? null}
        onClose={() => setToast(null)}
        variant={toast?.variant ?? 'info'}
        // 표준 자동 소멸 4초(D-09 §15.8) — 목업의 2.4초는 그대로 따르지 않는다
        duration={toast?.variant === 'warning' ? 4000 : undefined}
      />
    </div>
  );
}
